/**
 * getFeaturesV3 - Dynamic Feature Loading from Backend Module Definitions
 *
 * This module replaces the legacy getFeatures() and getFeaturesV2() functions
 * which contained 2000+ lines of hardcoded feature definitions.
 *
 * New Approach:
 * 1. Backend defines all features in ModuleRegistry
 * 2. Features are loaded via /api/v1/auth/context (already filtered by permissions)
 * 3. Frontend FeatureRegistry caches and serves features dynamically
 *
 * Benefits:
 * - Single source of truth (backend)
 * - Zero frontend hardcoding
 * - Automatic permission filtering
 * - Multi-vendor marketplace support
 * - Runtime module extensibility
 *
 * @see feature_registry.ts for storage
 * @see BACKEND_GETFEATURESV3_SPEC.md for backend contract
 */

import type { AuthContextResponse } from '@/types/auth_context';
import { featureRegistry, type FeatureDefinition } from './feature_registry';
export type { FeatureDefinition } from './feature_registry';
import { getAuthContextState, subscribeAuthContextState } from '@/uptk/core/api/auth_context_store';
import { useSyncExternalStore } from 'react';
import { findLandingMenu } from '@/uptk/core/modules/registry';
import type { ModuleMenuDefinition } from '@/uptk/core/modules/types';

const PORTAL_PREFIX_SEGMENTS = new Set(['portal', 'adminportal', 'make']);
type PortalRouteRoot = string;
type FeatureRouteBuildParams = {
  workspaceId?: string;
  year?: string;
  isPortal?: boolean;
  portalType?: PortalRouteRoot;
};

const ENTITY_ROUTE_ALIASES: Record<string, Record<string, string>> = {
  business: {
    beal: 'beals',
    bcm: 'bcms',
  },
  sourcing: {
    servicetype: 'servicetypes',
    contract: 'contracts',
    supplier: 'suppliers',
    supplierconsumption: 'supplierconsumptions',
  },
  finance: {
    budget: 'budgets',
    budgetline: 'budgetlines',
    budgetlinedetail: 'budgetlinedetails',
    invoice: 'invoices',
    budgetrequest: 'budgetrequests',
    budgetrequestextra: 'budgetrequestextras',
  },
  parameter: {
    glaccount: 'glaccounts',
    ca: 'cas',
  },
  application: {
    seal: 'seals',
    sealmodule: 'sealmodules',
    sla: 'slas',
  },
  infrastructure: {
    verumserver: 'verumservers',
    verumdbi: 'verumdbis',
    verummiddleware: 'verummiddlewares',
    verumpkg: 'verumpkgs',
  },
  project: {
    project: 'projects',
    projectreporting: 'projectreportings',
  },
};

const normalizePortalPrefixOrder = (segments: string[]): string[] => {
  if (segments.length === 0 || PORTAL_PREFIX_SEGMENTS.has(segments[0])) {
    return segments;
  }

  const portalPrefixIndex = segments.findIndex((segment, index) => {
    if (index === 0) {
      return false;
    }

    return PORTAL_PREFIX_SEGMENTS.has(segment);
  });

  if (portalPrefixIndex <= 0) {
    return segments;
  }

  const leadingContextSegments = segments.slice(0, portalPrefixIndex);
  if (!leadingContextSegments.every((segment) => segment.startsWith(':'))) {
    return segments;
  }

  return [
    segments[portalPrefixIndex],
    ...leadingContextSegments,
    ...segments.slice(portalPrefixIndex + 1),
  ];
};

const normalizeCanonicalFeatureRoute = (route: string): string => {
  const segments = normalizePortalPrefixOrder(
    route.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean)
  );
  if (segments.length === 0) {
    return '';
  }

  let domainIndex = 0;
  if (PORTAL_PREFIX_SEGMENTS.has(segments[0])) {
    domainIndex = 1;
  }

  while (domainIndex < segments.length && segments[domainIndex].startsWith(':')) {
    domainIndex += 1;
  }

  const domain = segments[domainIndex]?.toLowerCase();
  const entityIndex = domainIndex + 1;
  if (!domain || entityIndex >= segments.length) {
    return segments.join('/');
  }

  const entityAliases = ENTITY_ROUTE_ALIASES[domain];
  if (!entityAliases) {
    return segments.join('/');
  }

  const entity = segments[entityIndex].toLowerCase();
  const canonicalEntity = entityAliases[entity];
  if (!canonicalEntity) {
    return segments.join('/');
  }

  segments[entityIndex] = canonicalEntity;
  return segments.join('/');
};

let loadedAuthContextRef: AuthContextResponse | null = null;

const ensureRegistryLoaded = (authContext: AuthContextResponse | null) => {
  if (!authContext) {
    return;
  }

  if (loadedAuthContextRef !== authContext || !featureRegistry.hasFeatures()) {
    featureRegistry.loadFromAuthContext(authContext);
    loadedAuthContextRef = authContext;
  }
};

/**
 * Get features for one or more sites/domains
 *
 * @param authContext - Auth context from backend (includes modules with features)
 * @param site - Site name(s) (e.g., "finance", "project", ["finance", "project"])
 * @param currentUser - Optional user object (for additional client-side filtering)
 * @returns Array of feature definitions
 *
 * @example
 * // Single site
 * const features = getFeaturesV3(authContext, 'finance');
 *
 * @example
 * // Multiple sites
 * const features = getFeaturesV3(authContext, ['finance', 'project']);
 *
 * @example
 * // Get all portal features
 * const features = getFeaturesV3(authContext, 'portal');
 */
export const getFeaturesV3 = (
  authContext: AuthContextResponse | null,
  site: string | string[],
  currentUser?: unknown
): FeatureDefinition[] => {
  // Ensure feature registry is loaded
  if (!authContext) {
    console.warn('[getFeaturesV3] No auth context provided');
    return [];
  }

  ensureRegistryLoaded(authContext);

  // Normalize site to array
  const sites = Array.isArray(site) ? site : [site];

  // Collect features from all requested sites
  const featureMap = new Map<string, FeatureDefinition>();

  sites.forEach((s) => {
    if (!s || s.trim() === '') return;

    const siteFeatures = featureRegistry.getFeaturesForSite(s);
    siteFeatures.forEach((feature) => {
      featureMap.set(feature.key, feature);
    });
  });

  // Convert map to array (removes duplicates)
  const features = Array.from(featureMap.values());

  // Sort by order field
  return features.sort((a, b) => {
    const orderA = a.order ?? 999;
    const orderB = b.order ?? 999;
    return orderA - orderB;
  });
};

/**
 * Get a single feature by key
 *
 * @param featureKey - Feature identifier (e.g., "budget", "invoice")
 * @param authContext - Auth context from backend
 * @returns Feature definition or null
 *
 * @example
 * const budgetFeature = getFeatureV3('budget', authContext);
 * if (budgetFeature) {
 *   console.log(budgetFeature.displayName); // "features.budget.displayName"
 * }
 */
export const getFeatureV3 = (
  featureKey: string,
  authContext: AuthContextResponse | null
): FeatureDefinition | null => {
  if (!authContext) {
    return null;
  }

  ensureRegistryLoaded(authContext);

  return featureRegistry.getFeature(featureKey);
};

/**
 * Get all features for a specific module
 *
 * @param moduleCode - Module identifier (e.g., "finance-portfolio", "pmo")
 * @param authContext - Auth context from backend
 * @returns Array of feature definitions for the module
 *
 * @example
 * const financeFeatures = getModuleFeaturesV3('finance-portfolio', authContext);
 */
export const getModuleFeaturesV3 = (
  moduleCode: string,
  authContext: AuthContextResponse | null
): FeatureDefinition[] => {
  if (!authContext) {
    return [];
  }

  ensureRegistryLoaded(authContext);

  return featureRegistry.getModuleFeatures(moduleCode);
};

/**
 * React hook for dynamic feature loading with auth context
 *
 * @param site - Site name(s)
 * @returns Array of features
 *
 * @example
 * function FinancePage() {
 *   const features = useFeaturesV3('finance');
 *   return <FeatureGrid features={features} />;
 * }
 */
export const useFeaturesV3 = (site: string | string[]): FeatureDefinition[] => {
  const authContext = useSyncExternalStore(
    (onStoreChange) => subscribeAuthContextState(() => onStoreChange()),
    getAuthContextState,
    getAuthContextState
  );

  return getFeaturesV3(authContext, site);
};

/**
 * React hook for getting a single feature
 *
 * @param featureKey - Feature identifier
 * @returns Feature definition or null
 *
 * @example
 * function BudgetWidget() {
 *   const budgetFeature = useFeatureV3('budget');
 *   if (!budgetFeature) return null;
 *   return <FeatureCard feature={budgetFeature} />;
 * }
 */
export const useFeatureV3 = (featureKey: string): FeatureDefinition | null => {
  const authContext = useSyncExternalStore(
    (onStoreChange) => subscribeAuthContextState(() => onStoreChange()),
    getAuthContextState,
    getAuthContextState
  );

  return getFeatureV3(featureKey, authContext);
};

/**
 * Check if user has access to a feature
 * (Note: Backend already filters by permissions, this is for additional client checks)
 *
 * @param featureKey - Feature identifier
 * @param authContext - Auth context
 * @returns true if feature exists and is enabled
 */
export const hasFeatureAccessV3 = (
  featureKey: string,
  authContext: AuthContextResponse | null
): boolean => {
  const feature = getFeatureV3(featureKey, authContext);
  return feature !== null && feature.enabled !== false;
};

/**
 * Get features by parent/root
 *
 * @param rootKey - Parent feature key (e.g., "finance", "project")
 * @param authContext - Auth context
 * @returns Child features
 *
 * @example
 * const financeChildFeatures = getFeaturesByRootV3('finance', authContext);
 * // Returns: budget, budgetline, invoice, etc.
 */
export const getFeaturesByRootV3 = (
  rootKey: string,
  authContext: AuthContextResponse | null
): FeatureDefinition[] => {
  if (!authContext) {
    return [];
  }

  ensureRegistryLoaded(authContext);

  return featureRegistry.getAllFeatures().filter((feature) => feature.root === rootKey);
};

/**
 * Build feature route with path parameters
 *
 * @param feature - Feature definition
 * @param params - Optional parameters (workspaceId, year)
 * @returns Complete route string
 *
 * @example
 * const route = buildFeatureRoute(budgetFeature, { workspaceId: '1', year: '2024' });
 * // Returns: "/1/2024/finance/budget"
 */
export const buildFeatureRoute = (
  feature: FeatureDefinition,
  params?: FeatureRouteBuildParams
): string => {
  const legacyFeature = feature as FeatureDefinition & {
    includeworkspaceId?: boolean;
  };
  const includeWorkspaceId =
    feature.includeWorkspaceId ?? legacyFeature.includeworkspaceId ?? false;
  const requestedPortalRoot: PortalRouteRoot | undefined =
    params?.portalType ?? (params?.isPortal ? 'portal' : undefined);
  const isAdminPortalRequest = requestedPortalRoot === 'adminportal';
  const isPortalLikeRequest =
    requestedPortalRoot != null &&
    requestedPortalRoot.trim().length > 0 &&
    requestedPortalRoot !== 'adminportal';
  const baseRoute = isAdminPortalRequest
    ? feature.route ?? feature.portalRoute
    : isPortalLikeRequest
      ? feature.portalRoute ?? feature.route
      : feature.route ?? feature.portalRoute;
  if (!baseRoute) return '/';

  const normalizedBaseRoute = normalizeCanonicalFeatureRoute(baseRoute);
  if (!normalizedBaseRoute) return '/';

  // If backend route still uses placeholders, replace them directly.
  const hasPlaceholderWorkspace = normalizedBaseRoute.includes(':workspaceId');
  const hasPlaceholderYear = normalizedBaseRoute.includes(':year');

  if (hasPlaceholderWorkspace || hasPlaceholderYear) {
    let placeholderRoute = normalizedBaseRoute;

    if (hasPlaceholderWorkspace) {
      const resolvedWorkspaceId = params?.workspaceId ?? 'default';
      placeholderRoute = placeholderRoute.replace(':workspaceId', encodeURIComponent(resolvedWorkspaceId));
    }

    if (hasPlaceholderYear) {
      const resolvedYear = params?.year ?? String(new Date().getFullYear());
      placeholderRoute = placeholderRoute.replace(':year', encodeURIComponent(resolvedYear));
    }

    const placeholderSegments = placeholderRoute.split('/').filter(Boolean);
    const leadingPortalPrefix = PORTAL_PREFIX_SEGMENTS.has(placeholderSegments[0])
      ? placeholderSegments.shift()
      : undefined;
    const effectivePortalPrefix = requestedPortalRoot ?? leadingPortalPrefix;
    const segments = effectivePortalPrefix
      ? [effectivePortalPrefix, ...placeholderSegments]
      : placeholderSegments;

    return `/${segments.join('/')}`;
  }

  const routeSegments = normalizedBaseRoute.split('/').filter(Boolean);
  const leadingPortalPrefix = PORTAL_PREFIX_SEGMENTS.has(routeSegments[0])
    ? routeSegments.shift()
    : undefined;
  const effectivePortalPrefix = requestedPortalRoot ?? leadingPortalPrefix;
  const segments: string[] = [];

  if (effectivePortalPrefix) {
    segments.push(effectivePortalPrefix);
  }

  if (includeWorkspaceId) {
    const resolvedWorkspaceId = params?.workspaceId ?? 'default';
    segments.push(encodeURIComponent(resolvedWorkspaceId));
  }

  if (feature.includeYear) {
    const resolvedYear = params?.year ?? String(new Date().getFullYear());
    segments.push(encodeURIComponent(resolvedYear));
  }

  segments.push(...routeSegments);

  return `/${segments.join('/')}`;
};

/**
 * Filter features by permissions (additional client-side filtering)
 * Note: Backend already filters, this is for edge cases
 *
 * @param features - Features to filter
 * @param userPermissions - User's permissions array
 * @returns Filtered features
 */
export const filterFeaturesByPermissions = (
  features: FeatureDefinition[],
  userPermissions: string[]
): FeatureDefinition[] => {
  return features.filter((feature) => {
    const required = feature.requiredPermissions || [];
    if (required.length === 0) return true;

    // User must have ALL required permissions
    return required.every((perm) => userPermissions.includes(perm));
  });
};

/**
 * Group features by module
 *
 * @param features - Features to group
 * @returns Map of moduleCode -> features
 *
 * @example
 * const grouped = groupFeaturesByModule(features);
 * grouped.get('finance-portfolio'); // [budget, invoice, ...]
 */
export const groupFeaturesByModule = (
  features: FeatureDefinition[]
): Map<string, FeatureDefinition[]> => {
  const grouped = new Map<string, FeatureDefinition[]>();

  features.forEach((feature) => {
    const moduleCode = feature.moduleCode;
    const existing = grouped.get(moduleCode) || [];
    grouped.set(moduleCode, [...existing, feature]);
  });

  return grouped;
};

/**
 * Get feature statistics (for debugging/admin UI)
 *
 * @param authContext - Auth context
 * @returns Statistics object
 */
export const getFeatureStatisticsV3 = (authContext: AuthContextResponse | null) => {
  if (!authContext) {
    return {
      totalFeatures: 0,
      totalModules: 0,
      featuresByModule: {},
    };
  }

  ensureRegistryLoaded(authContext);

  const allFeatures = featureRegistry.getAllFeatures();
  const grouped = groupFeaturesByModule(allFeatures);

  const featuresByModule: Record<string, number> = {};
  grouped.forEach((features, moduleCode) => {
    featuresByModule[moduleCode] = features.length;
  });

  return {
    totalFeatures: allFeatures.length,
    totalModules: grouped.size,
    featuresByModule,
  };
};

/**
 * Get features filtered by landing menu
 * Unified helper for landing page feature resolution
 *
 * @param authContext - Auth context from backend
 * @param moduleCode - Module identifier (e.g., "finance-portfolio", "cbm")
 * @param menuKey - Optional menu filter (e.g., "budget", "invoice", "business")
 * @returns Feature definitions for the menu
 *
 * @example
 * // Get all finance features
 * const features = getMenuFeatures(authContext, "finance-portfolio", null);
 *
 * @example
 * // Get budget menu features only
 * const features = getMenuFeatures(authContext, "finance-portfolio", "budget");
 *
 * @example
 * // Get CBM business features
 * const features = getMenuFeatures(authContext, "cbm", "business");
 */
export const getMenuFeatures = (
  authContext: AuthContextResponse | null,
  moduleCode: string,
  menuKey?: string | null
): FeatureDefinition[] => {
  if (!authContext) {
    return [];
  }

  ensureRegistryLoaded(authContext);

  // If no menu filter, return all module features
  if (!menuKey) {
    return getModuleFeaturesV3(moduleCode, authContext);
  }

  // Find the module in AuthContext
  const module = authContext.modules?.find(m => m.code === moduleCode);
  if (!module) {
    // Fallback to registry-based lookup
    return getModuleFeaturesV3(moduleCode, authContext);
  }

  // Search landing menus first, then module menus for submenu-driven landing pages.
  const menu = findLandingMenu(
    (module.landingMenus || []) as ModuleMenuDefinition[],
    menuKey
  ) ?? findLandingMenu(
    (module.menus || []) as ModuleMenuDefinition[],
    menuKey
  );

  if (!menu || !menu.features || menu.features.length === 0) {
    // Fallback to all module features
    return getModuleFeaturesV3(moduleCode, authContext);
  }

  // Return features for the specific menu
  return menu.features
    .map(key => getFeatureV3(key, authContext))
    .filter((f): f is FeatureDefinition => f !== null);
};

/**
 * React hook version of getMenuFeatures
 * Use this in functional components for automatic updates
 *
 * @param moduleCode - Module identifier
 * @param menuKey - Optional menu filter
 * @returns Feature definitions
 *
 * @example
 * function FinancePage() {
 *   const menuKey = new URLSearchParams(location.search).get('menu');
 *   const features = useMenuFeatures('finance-portfolio', menuKey);
 *   return <FeatureGrid features={features} />;
 * }
 */
export const useMenuFeatures = (
  moduleCode: string,
  menuKey?: string | null
): FeatureDefinition[] => {
  const authContext = useSyncExternalStore(
    (onStoreChange) => subscribeAuthContextState(() => onStoreChange()),
    getAuthContextState,
    getAuthContextState
  );

  return getMenuFeatures(authContext, moduleCode, menuKey);
};
